import { useState, useCallback, useEffect, useRef } from 'react';
import { SortingState } from '@tanstack/react-table';
import request from 'lib/request';
import type {
  MissingWellAssignment,
  MissingWellAssignmentsResponse,
} from './types';

const PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 300;

interface UseMissingWellAssignmentsReturn {
  rows: MissingWellAssignment[];
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  totalCount: number;
  fullyCoveredCount: number;
  fetchNextPage: () => void;
  setSearch: (search: string) => void;
  setSort: (sorting: SortingState) => void;
}

export const useMissingWellAssignments =
  (): UseMissingWellAssignmentsReturn => {
    const [rows, setRows] = useState<MissingWellAssignment[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [hasMore, setHasMore] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [fullyCoveredCount, setFullyCoveredCount] = useState(0);
    const [offset, setOffset] = useState(0);

    const [currentSearch, setCurrentSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [sortColumn, setSortColumn] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    const abortControllerRef = useRef<AbortController | null>(null);
    const isFetchingRef = useRef(false);

    const fetchPage = useCallback(
      async (pageOffset: number, append: boolean) => {
        if (isFetchingRef.current) return;
        isFetchingRef.current = true;

        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;

        if (!append) {
          setIsLoading(true);
        }

        const params = new URLSearchParams({
          limit: String(PAGE_SIZE),
          offset: String(pageOffset),
        });
        if (debouncedSearch) params.set('search', debouncedSearch);
        if (sortColumn) {
          params.set('sort_column', sortColumn);
          params.set('sort_direction', sortDirection);
        }

        try {
          const response = await request.get<MissingWellAssignmentsResponse>(
            `/admin/missing_well_assignments?${params.toString()}`,
            controller
          );

          if (controller.signal.aborted) return;

          if ('error' in response) {
            if ((response as { error: string }).error === 'AbortError') return;
            setError((response as { error: string }).error);
            setIsLoading(false);
            isFetchingRef.current = false;
            return;
          }

          const data = response as MissingWellAssignmentsResponse;
          if (append) {
            setRows((prev) => [...prev, ...data.wells]);
          } else {
            setRows(data.wells);
          }
          setTotalCount(data.meta.totalCount);
          setFullyCoveredCount(data.meta.fullyCoveredCount);
          setHasMore(data.meta.hasMore);
          setOffset(pageOffset + PAGE_SIZE);
          setError(null);
          setIsLoading(false);
          isFetchingRef.current = false;
        } catch {
          if (controller.signal.aborted) return;
          setError('Failed to fetch data');
          setIsLoading(false);
          isFetchingRef.current = false;
        }
      },
      [debouncedSearch, sortColumn, sortDirection]
    );

    // Initial fetch and re-fetch on search/sort changes
    useEffect(() => {
      setRows([]);
      setOffset(0);
      setHasMore(false);
      fetchPage(0, false);

      return () => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        isFetchingRef.current = false;
      };
    }, [fetchPage]);

    const fetchNextPage = useCallback(() => {
      if (!hasMore || isFetchingRef.current) return;
      fetchPage(offset, true);
    }, [hasMore, offset, fetchPage]);

    const setSearch = useCallback(
      (search: string) => {
        if (search === currentSearch) return;
        setCurrentSearch(search);

        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
          setDebouncedSearch(search);
        }, SEARCH_DEBOUNCE_MS);
      },
      [currentSearch]
    );

    const setSort = useCallback((sorting: SortingState) => {
      if (sorting.length === 0) {
        setSortColumn(null);
        setSortDirection('asc');
      } else {
        setSortColumn(sorting[0].id);
        setSortDirection(sorting[0].desc ? 'desc' : 'asc');
      }
    }, []);

    // Cleanup search timeout on unmount
    useEffect(() => {
      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }, []);

    return {
      rows,
      isLoading,
      error,
      hasMore,
      totalCount,
      fullyCoveredCount,
      fetchNextPage,
      setSearch,
      setSort,
    };
  };
